import { useEffect, useRef, useState } from "react";
import { Button } from "@ascleo/ui/components/ui/button";
import { VolumeXIcon, Volume2Icon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { audioEmitter } from "../lib/audio";
import { frontdeskDiagnosticLogger } from "../lib/diagnostic-logger";
import { reportOperationalError } from "../lib/operational-error-telemetry";
import { usePlatform } from "../platform/context";

/**
 * AudioGuide component provides a mute toggle for the voice-over system.
 *
 * The actual audio playback is handled by the audio.ts module via mitt events.
 * This component:
 * - Loads the initial voice-over volume from platform settings
 * - Provides a mute/unmute toggle button for users
 * - Sends volume updates to the audio emitter
 */
export function AudioGuide() {
  const { t } = useTranslation();
  const platform = usePlatform();
  const [isMuted, setIsMuted] = useState(false);
  const volumeRef = useRef(50);
  const savedVolumeRef = useRef(50); // Store volume before muting

  // Load voice-over volume from settings on mount
  useEffect(
    function runLoadVolumeEffect() {
      const loadVolume = async () => {
        try {
          const settings = await platform.getStylingSettings();
          const initialVolume = settings.voiceOverVolume ?? 50;
          volumeRef.current = initialVolume;
          savedVolumeRef.current = initialVolume;
          // Send initial volume to audio emitter
          audioEmitter.emit("audio:set-volume", initialVolume);
        } catch (error) {
          frontdeskDiagnosticLogger.error(
            "Failed to load voice-over volume",
            error,
          );
          void reportOperationalError({
            action: "audio.settings-load",
            severity: "warning",
            source: "native",
            component: "AudioGuide",
            error,
          });
          // Use default volume
          audioEmitter.emit("audio:set-volume", 50);
        }
      };
      loadVolume();
    },
    [platform],
  );

  const handleVolumeToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (isMuted) {
      // Unmute: restore previous volume
      setIsMuted(false);
      audioEmitter.emit("audio:set-volume", savedVolumeRef.current);
    } else {
      // Mute: save current volume and set to 0
      savedVolumeRef.current = volumeRef.current;
      setIsMuted(true);
      audioEmitter.emit("audio:set-volume", 0);
    }
  };

  return (
    <div>
      <Button
        data-testid="audio-guide-toggle"
        type="button"
        variant="outline"
        size="lg"
        onClick={handleVolumeToggle}
        aria-label={
          isMuted
            ? t("self-service.audio-guide.off")
            : t("self-service.audio-guide.on")
        }
      >
        {isMuted ? (
          <VolumeXIcon className="size-6" />
        ) : (
          <Volume2Icon className="size-6" />
        )}
        <span className="ml-2 hidden @4xl/kiosk-header:inline">
          {isMuted
            ? t("self-service.audio-guide.off")
            : t("self-service.audio-guide.on")}
        </span>
      </Button>
    </div>
  );
}
